import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import { useAppDispatch, useAppSelector } from "store/store";

const CUSTOM_TURNOVER_COLUMNS: Extract<CalculationBaseColumn, "turnover_1" | "turnover_2">[] = [
    "turnover_1",
    "turnover_2",
];

/**
 * Display labels a company gave to the calculation-base columns. A company only fills turnover_1 /
 * turnover_2 once it has named them, so their label doubles as the flag telling whether the column
 * belongs to its sales model: screens listing sales only show the ones it named.
 */
export const useCalculationBaseColumnLabels = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { labels, labelsStatus } = useAppSelector((state) => state.calculationBaseColumnLabels);

    // Always refresh on mount so freshly configured turnover columns show up even if another screen
    // previously cached a stale list.
    useEffect(() => {
        dispatch(getCalculationBaseColumnLabels());
    }, [dispatch]);

    const labelByColumn = useMemo(
        () =>
            Object.fromEntries(labels.map((label) => [label.calculationBaseColumn, label.label])) as Partial<
                Record<CalculationBaseColumn, string>
            >,
        [labels],
    );

    const namedCustomTurnoverColumns = useMemo(
        () =>
            CUSTOM_TURNOVER_COLUMNS.flatMap((column) =>
                labelByColumn[column] ? [{ column, label: labelByColumn[column] as string }] : [],
            ),
        [labelByColumn],
    );

    const resolveLabel = (column: CalculationBaseColumn): string =>
        labelByColumn[column] ?? t(`CalculationBaseColumnLabels.Columns.${column}`);

    return { labelByColumn, namedCustomTurnoverColumns, labelsStatus, resolveLabel };
};
